import type { CSSProperties } from "react";

export type IconName =
  | "text"
  | "long_text"
  | "status"
  | "person"
  | "date"
  | "timeline"
  | "history"
  | "number"
  | "checkbox"
  | "tags"
  | "link"
  | "bold"
  | "italic"
  | "code"
  | "codeBlock"
  | "listBullet"
  | "listOrdered"
  | "indent"
  | "outdent"
  | "moveRight"
  | "cornerDownRight"
  | "table"
  | "quote"
  | "file"
  | "highlighter"
  | "kanban"
  | "search"
  | "bell"
  | "comment"
  | "reply"
  | "heart"
  | "plus"
  | "chevDown"
  | "chevUp"
  | "chevRight"
  | "chevLeft"
  | "dots"
  | "dotsV"
  | "drag"
  | "filter"
  | "sort"
  | "layers"
  | "cmd"
  | "star"
  | "pin"
  | "archive"
  | "ext"
  | "arrow"
  | "refresh"
  | "undo"
  | "redo"
  | "code-xml"
  | "megaphone"
  | "box"
  | "notepad-text"
  | "briefcase"
  | "building"
  | "chart"
  | "headset"
  | "user-round-key"
  | "handshake"
  | "ship-wheel"
  | "notebook-text"
  | "lightbulb"
  | "x"
  | "check"
  | "hamburger"
  | "sliders"
  | "inbox"
  | "trash"
  | "edit"
  | "eye"
  | "wifi_off"
  | "globe"
  | "lock"
  | "group"
  | "share"
  | "collapse"
  | "expand"
  | "panelLeft"
  | "upload"
  | "copy"
  | "settings"
  | "moon"
  | "sun"
  | "logout"
  | "printer"
  | "maximize"
  | "minimize"
  | "rauten";

// The nine LogicLine "Rauten" (official CVI mark, exported from
// LogicLine_Rauten*.svg). Filled — not stroked like the glyphs below — so it
// takes the passed `color` as its fill (white on a workspace badge). Its own
// viewBox (53.86×42.52, wider than tall) letterboxes inside the square icon
// slot via the default preserveAspectRatio, so sizing matches the other icons.
const RAUTEN_VIEWBOX = "0 0 53.86 42.52";
const RAUTEN_PATHS = [
  "M19.99,14.52c-0.24,1.03-1.3,1.88-2.37,1.88H9.94c-1.06,0-1.73-0.85-1.49-1.88l0.92-3.95c0.24-1.03,1.3-1.88,2.37-1.88h7.68c1.06,0,1.73,0.85,1.49,1.88L19.99,14.52z",
  "M34.41,14.52c-0.24,1.03-1.3,1.88-2.37,1.88h-7.68c-1.06,0-1.73-0.85-1.49-1.88l0.92-3.95c0.24-1.03,1.3-1.88,2.37-1.88h7.68c1.06,0,1.73,0.85,1.49,1.88L34.41,14.52z",
  "M48.84,14.52c-0.24,1.03-1.3,1.88-2.37,1.88h-7.68c-1.06,0-1.73-0.85-1.49-1.88l0.92-3.95c0.24-1.03,1.3-1.88,2.37-1.88h7.68c1.06,0,1.73,0.85,1.49,1.88L48.84,14.52z",
  "M17.75,24.16c-0.24,1.03-1.3,1.88-2.37,1.88H7.7c-1.06,0-1.73-0.85-1.49-1.88l0.91-3.95c0.24-1.03,1.3-1.88,2.37-1.88h7.68c1.06,0,1.73,0.85,1.49,1.88L17.75,24.16z",
  "M32.18,24.16c-0.24,1.03-1.3,1.88-2.37,1.88h-7.68c-1.06,0-1.73-0.85-1.49-1.88l0.91-3.95c0.24-1.03,1.3-1.88,2.37-1.88h7.68c1.06,0,1.73,0.85,1.49,1.88L32.18,24.16z",
  "M46.61,24.16c-0.24,1.03-1.3,1.88-2.37,1.88h-7.68c-1.06,0-1.73-0.85-1.49-1.88l0.92-3.95c0.24-1.03,1.3-1.88,2.37-1.88h7.68c1.06,0,1.73,0.85,1.49,1.88L46.61,24.16z",
  "M15.52,33.79c-0.24,1.03-1.3,1.88-2.37,1.88H5.47c-1.06,0-1.73-0.85-1.49-1.88l0.92-3.95c0.24-1.03,1.3-1.88,2.37-1.88h7.68c1.06,0,1.73,0.85,1.49,1.88L15.52,33.79z",
  "M29.94,33.79c-0.24,1.03-1.3,1.88-2.37,1.88H19.9c-1.06,0-1.73-0.85-1.49-1.88l0.92-3.95c0.24-1.03,1.3-1.88,2.37-1.88h7.68c1.06,0,1.73,0.85,1.49,1.88L29.94,33.79z",
  "M44.37,33.79c-0.24,1.03-1.3,1.88-2.37,1.88h-7.68c-1.06,0-1.73-0.85-1.49-1.88l0.92-3.95c0.24-1.03,1.3-1.88,2.37-1.88h7.68c1.06,0,1.73,0.85,1.49,1.88L44.37,33.79z",
];

// `rauten` is rendered specially (multi-path, filled); the rest are single
// stroked paths, so it's excluded from this glyph map.
const ICONS: Record<Exclude<IconName, "rauten">, string> = {
  text: "M4 7h16M4 12h10M4 17h16",
  long_text: "M4 6h16M4 10h16M4 14h12M4 18h8",
  status: "M12 4a8 8 0 100 16 8 8 0 000-16zM12 8v4l3 2",
  person: "M16 14a4 4 0 10-8 0M12 11a3 3 0 110-6 3 3 0 010 6zM4 20a8 8 0 0116 0",
  // Lucide "handshake" — each subpath rewritten to an absolute M start so the
  // single-<path> renderer places all strokes correctly.
  handshake:
    "M11 17l2 2a1 1 0 1 0 3-3 M14 14l2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4 M21 3l1 11h-2 M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3 M3 4h8",
  date: "M3 8h18M3 8a2 2 0 012-2h14a2 2 0 012 2v11a2 2 0 01-2 2H5a2 2 0 01-2-2V8zM8 3v4M16 3v4",
  timeline: "M3 11h18M6 8v6M18 8v6M9 11h6",
  history: "M3 3v5h5M3.05 13a9 9 0 1 0 2.6-6.36L3 8M12 7v5l3 2",
  number: "M5 9h14M5 15h14M9 4l-2 16M17 4l-2 16",
  checkbox:
    "M3.5 6.5a3 3 0 013-3h11a3 3 0 013 3v11a3 3 0 01-3 3h-11a3 3 0 01-3-3v-11zM8 12.5l2.5 2.5L16 9",
  tags: "M3 12V5a2 2 0 012-2h7l9 9-9 9-9-9zM8 8.5a1 1 0 100-2 1 1 0 000 2z",
  link: "M10 14a4 4 0 015.66 0l3-3a4 4 0 00-5.66-5.66l-2 2M14 10a4 4 0 00-5.66 0l-3 3a4 4 0 005.66 5.66l2-2",
  file: "M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6zM14 3v6h6",
  bold: "M7 5h6a3.5 3.5 0 010 7H7zM7 12h7a3.5 3.5 0 010 7H7z",
  italic: "M19 5h-7M12 19H5M15 5L9 19",
  code: "M16 18l5-6-5-6M8 6l-5 6 5 6",
  codeBlock:
    "M4 4h16a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1zM10 9l-3 3 3 3M14 9l3 3-3 3",
  listBullet: "M9 6h12M9 12h12M9 18h12M4.5 6h.01M4.5 12h.01M4.5 18h.01",
  listOrdered: "M10 6h11M10 12h11M10 18h11M4 8V5l-1 1M3.5 13.5h1.5a.5.5 0 010 1L3.5 16v.5h2",
  indent: "M3 8l4 4-4 4M21 12H11M21 6H11M21 18H11",
  outdent: "M7 8l-4 4 4 4M21 12H11M21 6H11M21 18H11",
  moveRight: "M5 12h14M13 6l6 6-6 6",
  // Lucide "corner-down-right" — an arrow turning down and to the right.
  cornerDownRight: "M15 10l5 5-5 5M4 4v7a4 4 0 0 0 4 4h12",
  table: "M3 3h18v18H3zM3 9h18M3 15h18M9 3v18M15 3v18",
  quote:
    "M10 11H5a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 011 1v6a4 4 0 01-4 4M20 11h-5a1 1 0 01-1-1V7a1 1 0 011-1h3a1 1 0 011 1v6a4 4 0 01-4 4",
  // Marker pen: nib + body, over an underline. Stroke-only, like its neighbours.
  highlighter: "M9 11l-4 4v3h3l4-4M9 11l5-5 4 4-5 5M13 6l3-3 4 4-3 3M3 21h18",
  kanban: "M4 4h4v16H4zM10 4h4v10h-4zM16 4h4v7h-4z",
  search: "M11 4a7 7 0 105 12l4 4M11 4a7 7 0 017 7",
  bell: "M6 8a6 6 0 1112 0c0 7 3 7 3 7H3s3 0 3-7M10 21a2 2 0 004 0",
  comment: "M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z",
  reply: "M9 14L4 9l5-5M4 9h9a5 5 0 015 5v4",
  heart:
    "M12 21l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.18L12 21z",
  plus: "M12 5v14M5 12h14",
  chevDown: "M6 9l6 6 6-6",
  chevUp: "M6 15l6-6 6 6",
  chevRight: "M9 6l6 6-6 6",
  chevLeft: "M15 6l-6 6 6 6",
  dots: "M6 12h.01M12 12h.01M18 12h.01",
  dotsV: "M12 6h.01M12 12h.01M12 18h.01",
  drag: "M9 7h.01M9 12h.01M9 17h.01M15 7h.01M15 12h.01M15 17h.01",
  filter: "M3 5h18l-7 9v6l-4-2v-4L3 5z",
  sort: "M3 7h13M3 12h9M3 17h5M17 6v12m0 0l-3-3m3 3l3-3",
  layers: "M12 3l9 5-9 5-9-5 9-5zM3 13l9 5 9-5M3 17l9 5 9-5",
  cmd: "M9 6a3 3 0 11-3 3h12a3 3 0 11-3-3v12a3 3 0 113-3H6a3 3 0 113 3V6z",
  star: "M12 3l2.6 6.2L21 10l-5 4.4L17.5 21 12 17.5 6.5 21 8 14.4 3 10l6.4-.8L12 3z",
  pin: "M12 2v10m0 0l-4 4h8l-4-4zM8 8l8 0",
  archive:
    "M3 5a1 1 0 011-1h16a1 1 0 011 1v3H3V5zM4 8h16v11a1 1 0 01-1 1H5a1 1 0 01-1-1V8zM10 12h4",
  ext: "M14 4h6v6M20 4l-9 9M19 13v6a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1h6",
  arrow: "M5 12h14m0 0l-5-5m5 5l-5 5",
  // maximize-2 / minimize-2 (Lucide) — expand ↔ collapse the detail panel (⑤).
  maximize: "M15 3h6v6M21 3l-7 7M9 21H3v-6M3 21l7-7",
  minimize: "M4 14h6v6M10 14l-7 7M20 10h-6V4M14 10l7-7",
  refresh: "M3 12a9 9 0 0115-6.7L21 8M21 4v4h-4M21 12a9 9 0 01-15 6.7L3 16M3 20v-4h4",
  undo: "M9 14L4 9l5-5M4 9h11a6 6 0 010 12h-4",
  redo: "M15 14l5-5-5-5M20 9H9a6 6 0 000 12h4",
  "code-xml": "M18 16l4-4-4-4M6 8l-4 4 4 4M14.5 4l-5 16",
  // Lucide "user-round-key" (added after our lucide-react 0.453) — hand-drawn:
  // a round-user on the left + a key (ring + stem) lower-right.
  "user-round-key":
    "M10 11a3 3 0 100-6 3 3 0 000 6M4 20a6 6 0 0110.5-4M18 13a2.5 2.5 0 100 5 2.5 2.5 0 000-5M20.2 12.2l2-2M21.5 13.5l1 1",
  megaphone: "M3 11l18-5v12L3 14v-3zM11.6 16.8a3 3 0 11-5.8-1.6",
  box: "M12 2l9 5v10l-9 5-9-5V7l9-5zM3 7l9 5 9-5M12 12v10",
  "notepad-text":
    "M8 2v3M16 2v3M5 5h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V6a1 1 0 011-1zM8 11h8M8 15h5",
  briefcase:
    "M3 8a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8zM8 6V5a2 2 0 012-2h4a2 2 0 012 2v1M3 12h18",
  building:
    "M4 22V4a1 1 0 011-1h9a1 1 0 011 1v18M15 9h4a1 1 0 011 1v11M8 7h2M8 11h2M8 15h2M3 22h18",
  chart: "M3 3v18h18M8 16v-5M13 16V8M18 16v-9",
  headset:
    "M3 11h3a2 2 0 012 2v3a2 2 0 01-2 2H4a1 1 0 01-1-1v-5a9 9 0 0118 0v5a1 1 0 01-1 1h-2a2 2 0 01-2-2v-3a2 2 0 012-2h3M21 16v2a4 4 0 01-4 4h-5",
  // Lucide "ship-wheel" — outer rim + inner hub as arc-circles, plus 8 spokes,
  // each subpath rewritten to an absolute M so the single-<path> renderer works.
  "ship-wheel":
    "M4 12a8 8 0 1 0 16 0a8 8 0 1 0-16 0M10 12a2 2 0 1 0 4 0a2 2 0 1 0-4 0M12 2v7.5M22 12h-7.5M12 14.5V22M9.5 12H2M19 5l-5.23 5.23M19 19l-5.23-5.23M10.23 13.77L5 19M10.23 10.23L5 5",
  // Lucide "notebook-text" — spine ticks + rounded cover + 3 text lines, one path.
  "notebook-text":
    "M2 6h4M2 10h4M2 14h4M2 18h4M6 2H18A2 2 0 0 1 20 4V20A2 2 0 0 1 18 22H6A2 2 0 0 1 4 20V4A2 2 0 0 1 6 2ZM9.5 8h5M9.5 12H16M9.5 16H14",
  // Lucide "lightbulb" — bulb glass + filament base and two socket ticks, each
  // subpath rewritten to an absolute M so the single-<path> renderer works.
  lightbulb:
    "M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5M9 18h6M10 22h4",
  x: "M6 6l12 12M18 6L6 18",
  check: "M5 12l4 4 10-12",
  hamburger: "M4 7h16M4 12h16M4 17h16",
  sliders:
    "M4 10h6M14 10h6M4 18h12M18 18h2M4 6h2M10 6h10M8 8a2 2 0 100-4 2 2 0 000 4zM12 12a2 2 0 100-4 2 2 0 000 4zM18 20a2 2 0 100-4 2 2 0 000 4z",
  inbox:
    "M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z",
  trash: "M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M6 6l1 14a2 2 0 002 2h6a2 2 0 002-2l1-14",
  edit: "M11 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2v-5M18.4 2.6a2 2 0 012.8 2.8L12 14.6 8 16l1.4-4 9-9z",
  eye: "M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7zM12 15a3 3 0 100-6 3 3 0 000 6z",
  wifi_off:
    "M2 8.8A14 14 0 015.7 7M11.6 13.5a4 4 0 015.7-.5M22 8.8a14 14 0 00-3.7-1.8M8.5 16.4a8 8 0 014.4-2.3M12 20l.01 0M2 2l20 20",
  globe: "M12 22a10 10 0 100-20 10 10 0 000 20zM2 12h20M12 2a14 14 0 010 20M12 2a14 14 0 000 20",
  lock: "M7 10V7a5 5 0 0 1 10 0v3M5 10h14v10H5zM12 14v2",
  group: "M3 6h8M3 12h8M3 18h8M14 4l5 5-5 5M19 9v9a2 2 0 01-2 2h-3",
  share:
    "M18 5a3 3 0 10-3 3 3 3 0 003-3zM6 12a3 3 0 10-3 3 3 3 0 003-3zM18 19a3 3 0 10-3 3 3 3 0 003-3M8.6 13.5l6.8 4M15.4 6.5l-6.8 4",
  collapse: "M4 14h6v6M20 10h-6V4M14 10l7-7M10 14l-7 7",
  expand: "M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7",
  // Sidebar toggle. Deliberately NOT `collapse`/`expand` — those are the
  // diagonal fullscreen arrows, which read as "maximise", not "hide the rail".
  panelLeft: "M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2zM9 3v18",
  upload: "M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12",
  copy: "M9 4h9a2 2 0 012 2v9M5 8a2 2 0 012-2h8a2 2 0 012 2v10a2 2 0 01-2 2H7a2 2 0 01-2-2V8z",
  settings:
    "M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z",
  moon: "M21 12.79A9 9 0 1111.21 3a7 7 0 009.79 9.79z",
  sun: "M12 4V2m0 20v-2M4 12H2m20 0h-2M5.6 5.6L4.2 4.2m15.6 15.6l-1.4-1.4M5.6 18.4l-1.4 1.4M19.8 4.2l-1.4 1.4M12 8a4 4 0 100 8 4 4 0 000-8z",
  logout: "M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9",
  printer:
    "M6 9V2h12v7M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2M6 14h12v8H6z",
};

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  stroke?: number;
  /** Fill colour (e.g. a filled heart when liked). Defaults to "none". */
  fill?: string;
  style?: CSSProperties;
  className?: string;
}

export function Icon({
  name,
  size = 16,
  color = "currentColor",
  stroke = 1.6,
  fill = "none",
  style,
  className,
}: IconProps) {
  if (name === "rauten") {
    return (
      <svg
        width={size}
        height={size}
        viewBox={RAUTEN_VIEWBOX}
        fill={color}
        style={{ flexShrink: 0, ...style }}
        aria-hidden="true"
        className={className}
      >
        {RAUTEN_PATHS.map((d, i) => (
          <path key={i} d={d}></path>
        ))}
      </svg>
    );
  }
  const d = ICONS[name];
  if (!d) return null;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={color}
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }}
      aria-hidden="true"
      className={className}
    >
      <path d={d}></path>
    </svg>
  );
}
